import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { BP } from '../../utils/breakpoints';

const CX = 123.5;
const CY = 123.5;

export default class Footer {
  constructor(el) {
    this.el    = el;
    this.logos = [...el.querySelectorAll('.c-footer__logo__wrapper svg')];
    this.clock = el.querySelector('.c-footer__clock');

    this.logos.forEach((logo) => this.setupLogoReveal(logo));

    if (!this.clock) return;

    // Wheel roll physics
    this.targetX   = 0;
    this.currentX  = 0;
    this.velocityX = 0;
    this.rotation  = 0;

    this.SPRING  = 0.04;
    this.DAMPING = 0.78;
    this.RADIUS  = 123.5;

    // Compute initial hand angles from real current time
    const now = new Date();
    const h   = now.getHours() % 12;
    const m   = now.getMinutes();
    const s   = now.getSeconds();
    const ms  = now.getMilliseconds();

    // Each hand has:
    //   angle     – current SVG rotation delta from its drawn rest angle (deg)
    //   vel       – angular velocity (deg/frame @ 60fps reference)
    //   inertia   – how strongly rolling clockDelta drives the hand's velocity
    //   damp      – friction per 60fps frame (rescaled by dt at runtime)
    //   degPerMs  – real-time tick rate (deg/ms), frame-rate independent
    //   restAngle – hand's drawn position in the SVG design (deg from 12 o'clock)
    this.hands = [
      {
        el:        this.clock.querySelector('.hour-hand'),
        restAngle: 45.5,
        angle:     (h + m / 60) / 12 * 360 - 45.5,
        vel:       0,
        inertia:   0.2,
        damp:      0.93,
        degPerMs:  360 / 43200000,
      },
      {
        el:        this.clock.querySelector('.min-hand'),
        restAngle: 289.3,
        angle:     (m + s / 60) / 60 * 360 - 289.3,
        vel:       0,
        inertia:   0.2,
        damp:      0.95,
        degPerMs:  360 / 3600000,
      },
      {
        el:        this.clock.querySelector('.second-hand-group'),
        restAngle: 135,
        angle:     (s + ms / 1000) / 60 * 360 - 135,
        vel:       0,
        inertia:   0.2,
        damp:      0.97,
        degPerMs:  360 / 60000,
      },
    ];

    this.lastT = 0;
    this.FRAME_MS = 1000 / 60;

    this._footerCenterX = null;
    this._clampLeft  = null;
    this._clampRight = null;
    this.RADIUS = 0; // computed from actual rendered size in updateMetrics()
    this.raf = null;

    this.contactEl = el.querySelector('.c-footer__contact');
    this.legalsEl  = el.querySelector('.c-footer__legals');

    this.onMouseMove    = this.onMouseMove.bind(this);
    this.tick           = this.tick.bind(this);
    this.updateMetrics  = this.updateMetrics.bind(this);
    this.init();
  }

  updateMetrics() {
    // offsetWidth ignores the roll transform: the rotated clock's bounding rect
    // is wider than the clock itself.
    this.RADIUS = this.clock.offsetWidth / 2;
    this._footerCenterX = null; // invalidate so onMouseMove recomputes

    // Clamp the clock against the actual rendered text edges, not the container
    // padding — pick the rightmost left-aligned edge and the leftmost right-aligned
    // edge across contact + legals, so wider items (e.g. "PRIVACY POLICY") win.
    const footerRect = this.el.getBoundingClientRect();
    const centerX    = footerRect.left + footerRect.width / 2;

    let leftEdge  = -Infinity; // rightmost edge of any left-side text
    let rightEdge =  Infinity; // leftmost edge of any right-side text

    const consider = (node) => {
      if (!node) return;
      const b = node.getBoundingClientRect();
      if (!b.width) return;
      const mid = b.left + b.width / 2;
      if (mid < centerX) leftEdge  = Math.max(leftEdge,  b.right);
      else               rightEdge = Math.min(rightEdge, b.left);
    };

    if (this.contactEl) for (const c of this.contactEl.children) consider(c);
    if (this.legalsEl)  for (const c of this.legalsEl.children)  consider(c);

    this._clampLeft  = isFinite(leftEdge)  ? leftEdge  : null;
    this._clampRight = isFinite(rightEdge) ? rightEdge : null;
  }

  init() {
    this.updateMetrics();
    window.addEventListener('resize', this.updateMetrics);

    this.mq = window.matchMedia(BP.tabletAndMobile);
    this.mq.addEventListener('change', (e) => e.matches ? this.stopClock() : this.startClock());

    if (!this.mq.matches) this.startClock();
  }

  startClock() {
    window.addEventListener('mousemove', this.onMouseMove);
    if (!this.raf) this.tick();
  }

  stopClock() {
    window.removeEventListener('mousemove', this.onMouseMove);
    cancelAnimationFrame(this.raf);
    this.raf = null;

    // Back to rest, so the clock isn't left offset/rotated on tablet/mobile and
    // doesn't resume with stale momentum on desktop.
    this.targetX = this.currentX = this.velocityX = this.rotation = 0;
    this.lastT = 0;
    this.clock.style.removeProperty('transform');
  }

  onMouseMove(e) {
    const r = this.el.getBoundingClientRect();
    this._footerCenterX = r.left + r.width / 2;

    const fallback = r.width / 2 - this.RADIUS;
    let minX = -fallback;
    let maxX =  fallback;

    if (this._clampLeft  !== null) minX = this._clampLeft  - this._footerCenterX + this.RADIUS;
    if (this._clampRight !== null) maxX = this._clampRight - this._footerCenterX - this.RADIUS;

    this.targetX = Math.max(minX, Math.min(maxX, e.clientX - this._footerCenterX));
  }

  tick(now = performance.now()) {
    const dt = this.lastT ? Math.min(now - this.lastT, 100) : this.FRAME_MS;
    this.lastT = now;
    const dtScale = dt / this.FRAME_MS;

    // ── wheel roll ───────────────────────────────────────────────────────────
    this.velocityX += (this.targetX - this.currentX) * this.SPRING * dtScale;
    this.velocityX *= Math.pow(this.DAMPING, dtScale);
    this.currentX  += this.velocityX * dtScale;

    // Angular velocity of the rolling wheel (deg this frame)
    const clockDelta = (this.velocityX * dtScale / this.RADIUS) * (180 / Math.PI);
    this.rotation   += clockDelta;

    this.clock.style.transform = `translateX(${this.currentX}px) rotate(${this.rotation}deg)`;

    // ── hands ─────────────────────────────────────────────────────────────────
    for (const hand of this.hands) {
      // Inertia: rolling drives each hand independently at its own coupling rate
      hand.vel   += clockDelta * hand.inertia;
      hand.vel   *= Math.pow(hand.damp, dtScale);
      // Real-time tick + inertia coast
      hand.angle += hand.degPerMs * dt + hand.vel * dtScale;
      hand.el.setAttribute('transform', `rotate(${hand.angle}, ${CX}, ${CY})`);
    }

    this.raf = requestAnimationFrame(this.tick);
  }

  setupLogoReveal(logo) {
    if (!logo) return;
    const paths = [...logo.querySelectorAll('path')];
    if (!paths.length) return;

    paths.sort((a, b) => a.getBBox().x - b.getBBox().x);

    const offset = logo.viewBox.baseVal.height;
    gsap.set(paths, { y: offset });

    const anim = gsap.to(paths, {
      y: 0,
      duration: 1.2,
      ease: 'expo.out',
      stagger: 0.08,
      paused: true,
    });

    ScrollTrigger.create({
      trigger: logo,
      start: 'top 80%',
      onEnter: () => anim.play(),
    });
  }

  destroy() {
    this.stopClock();
    window.removeEventListener('resize', this.updateMetrics);
  }
}
